import { useEffect, useMemo, useState } from 'react';
import { Link, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import DashboardShell from './DashboardShell';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { Booking, Offer, Vendor } from '../../lib/database.types';
import { NEIGHBORHOODS } from '../../lib/database.types';
import { formatDate, formatMoney, formatTime } from '../../lib/format';
import VendorMap from '../../components/VendorMap';

const tabs = [
  { label: 'Home', href: '/dashboard' },
  { label: 'My bookings', href: '/dashboard/bookings' },
  { label: 'Team', href: '/dashboard/team' },
  { label: 'Payment', href: '/dashboard/payment' },
];

export default function MemberDashboard() {
  return (
    <DashboardShell role="member" tabs={tabs}>
      <Routes>
        <Route index element={<Home />} />
        <Route path="bookings" element={<Bookings />} />
        <Route path="team" element={<Team />} />
        <Route path="payment" element={<Payment />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </DashboardShell>
  );
}

type BookingWithJoins = Booking & { vendor: Vendor | null; offer: Offer | null };
type VendorCard = Vendor & { offers: Offer[]; minPriceCents: number | null };

const DAY_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const VIBE_CHIPS: { label: string; icon: string; match: string[] }[] = [
  { label: 'Cocktails',    icon: '🍸', match: ['cocktail', 'cocktails', 'craft cocktails'] },
  { label: 'Rooftop',      icon: '🌃', match: ['rooftop', 'roof'] },
  { label: 'Wine bar',     icon: '🍷', match: ['wine'] },
  { label: 'Speakeasy',    icon: '🗝️', match: ['speakeasy', 'hidden'] },
  { label: 'Patio',        icon: '🌿', match: ['patio', 'outdoor', 'garden'] },
  { label: 'Live music',   icon: '🎷', match: ['live music', 'jazz', 'music'] },
  { label: 'Dive',         icon: '🍺', match: ['dive', 'beer'] },
  { label: 'Date night',   icon: '🌹', match: ['date', 'romantic', 'intimate'] },
  { label: 'Sports',       icon: '🏈', match: ['sports', 'tv'] },
];

function dayLabel(iso: string): string {
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  if (iso === today) return 'Tonight';
  if (iso === tomorrow) return 'Tomorrow';
  return formatDate(iso);
}

function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [upcoming, setUpcoming] = useState<BookingWithJoins[]>([]);
  const [vendors, setVendors] = useState<VendorCard[]>([]);
  const [loading, setLoading] = useState(true);

  // Search state lives in the URL so it survives navigation: clicking a vendor
  // → reading the location detail → coming back keeps the same date and
  // headcount, and forwarding to checkout pre-fills them. Local view toggles
  // (list vs. map, vibe filter) stay component-local — they aren't worth
  // serializing into every link.
  const todayISO = new Date().toISOString().slice(0, 10);
  const date = searchParams.get('date') || todayISO;
  const headcount = Number(searchParams.get('headcount')) || 10;
  const neighborhood = searchParams.get('neighborhood') || 'All';
  const search = searchParams.get('q') || '';
  const [vibe, setVibe] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'map'>('list');

  function updateParam(key: string, value: string, defaultValue?: string) {
    const next = new URLSearchParams(searchParams);
    if (!value || (defaultValue !== undefined && value === defaultValue)) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }
  const setDate = (v: string) => updateParam('date', v, todayISO);
  const setHeadcount = (updater: (n: number) => number) => {
    const next = Math.max(1, Math.min(500, updater(headcount)));
    updateParam('headcount', String(next), '10');
  };
  const setNeighborhood = (v: string) => updateParam('neighborhood', v, 'All');
  const setSearch = (v: string) => updateParam('q', v, '');

  const linkSuffix = useMemo(() => {
    const params = new URLSearchParams();
    if (date !== todayISO) params.set('date', date);
    if (headcount !== 10) params.set('headcount', String(headcount));
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [date, headcount, todayISO]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const today = new Date().toISOString().slice(0, 10);

      const [{ data: bookings }, { data: vendorRows }, { data: offerRows }] = await Promise.all([
        supabase
          .from('ga_bookings')
          .select('*')
          .gte('booking_date', today)
          .neq('status', 'cancelled')
          .order('booking_date', { ascending: true })
          .limit(5),
        supabase.from('ga_vendors').select('*').eq('status', 'active').order('name'),
        supabase.from('ga_offers').select('*').eq('status', 'active'),
      ]);

      const vendorList = (vendorRows ?? []) as Vendor[];
      const offerList = (offerRows ?? []) as Offer[];

      const vendorById = new Map(vendorList.map((v) => [v.id, v]));
      const offersByVendor = new Map<string, Offer[]>();
      for (const o of offerList) {
        const arr = offersByVendor.get(o.vendor_id) ?? [];
        arr.push(o);
        offersByVendor.set(o.vendor_id, arr);
      }

      const cards: VendorCard[] = vendorList.map((v) => {
        const offers = offersByVendor.get(v.id) ?? [];
        const minPriceCents = offers.length > 0 ? Math.min(...offers.map((o) => o.price_cents)) : null;
        return { ...v, offers, minPriceCents };
      });
      setVendors(cards);

      const bookingList = (bookings ?? []) as Booking[];
      if (bookingList.length > 0) {
        const offerIds = [...new Set(bookingList.map((b) => b.offer_id))];
        const { data: bookingOffers } = await supabase.from('ga_offers').select('*').in('id', offerIds);
        const offerMap = new Map(((bookingOffers ?? []) as Offer[]).map((o) => [o.id, o]));
        setUpcoming(
          bookingList.map((b) => ({
            ...b,
            vendor: vendorById.get(b.vendor_id) ?? null,
            offer: offerMap.get(b.offer_id) ?? null,
          })),
        );
      } else {
        setUpcoming([]);
      }

      setLoading(false);
    })();
  }, [user]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const day = date ? DAY_KEYS[new Date(date + 'T00:00:00').getDay()] : null;
    const vibeMatchers = vibe ? VIBE_CHIPS.find((c) => c.label === vibe)?.match ?? [] : [];
    return vendors.filter((v) => {
      if (neighborhood !== 'All' && v.neighborhood !== neighborhood) return false;
      if (q) {
        const hay = `${v.name} ${v.neighborhood ?? ''} ${v.address ?? ''} ${v.vibes.join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (vibeMatchers.length > 0) {
        const vibesLower = v.vibes.map((x) => x.toLowerCase());
        const hit = vibeMatchers.some((m) => vibesLower.some((vv) => vv.includes(m)));
        if (!hit) return false;
      }
      const matching = v.offers.filter((o) => {
        if (headcount < o.headcount_min || headcount > o.headcount_max) return false;
        if (day && o.days_of_week.length > 0 && !o.days_of_week.includes(day)) return false;
        return true;
      });
      return matching.length > 0;
    });
  }, [vendors, neighborhood, search, headcount, date, vibe]);

  const neighborhoodCount = new Set(filtered.map((v) => v.neighborhood).filter(Boolean)).size;
  const cheapest = filtered.reduce<number | null>((min, v) => v.minPriceCents != null && (min == null || v.minPriceCents < min) ? v.minPriceCents : min, null);

  return (
    <div>
      <UpcomingRail bookings={upcoming} loading={loading} />

      {/* Search pill */}
      <div className="ga-search-pill" style={{ marginTop: 12 }}>
        <div className="ga-search-cell">
          <span className="ga-search-label">When</span>
          <input
            type="date"
            value={date}
            min={todayISO}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Date"
          />
        </div>
        <div className="ga-search-divider" />
        <div className="ga-search-cell">
          <span className="ga-search-label">Who</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className="ga-stepper"
              aria-label="Decrease headcount"
              onClick={() => setHeadcount((n) => n - 1)}
            >−</button>
            <span style={{ minWidth: 72, fontWeight: 600 }}>{headcount} people</span>
            <button
              type="button"
              className="ga-stepper"
              aria-label="Increase headcount"
              onClick={() => setHeadcount((n) => n + 1)}
            >+</button>
          </div>
        </div>
        <div className="ga-search-divider" />
        <div className="ga-search-cell">
          <span className="ga-search-label">Where</span>
          <select value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} aria-label="Neighborhood">
            <option value="All">Anywhere in NYC</option>
            {NEIGHBORHOODS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="ga-search-divider" />
        <div className="ga-search-cell ga-search-cell-search">
          <span className="ga-search-label">Search</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="A name or a vibe…"
            aria-label="Search bars"
          />
        </div>
      </div>

      {/* Vibe chips */}
      <div className="ga-vibe-scroll" style={{ marginTop: 24 }}>
        <button
          className={`ga-vibe-chip ${vibe === null ? 'ga-vibe-chip--active' : ''}`}
          onClick={() => setVibe(null)}
        >
          <span style={{ fontSize: 16 }}>✨</span>
          <span>All vibes</span>
        </button>
        {VIBE_CHIPS.map((c) => (
          <button
            key={c.label}
            className={`ga-vibe-chip ${vibe === c.label ? 'ga-vibe-chip--active' : ''}`}
            onClick={() => setVibe(vibe === c.label ? null : c.label)}
          >
            <span style={{ fontSize: 16 }}>{c.icon}</span>
            <span>{c.label}</span>
          </button>
        ))}
      </div>

      {/* Stats + view toggle */}
      <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div className="mono" style={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
          {loading
            ? 'Loading…'
            : (
              <>
                <span style={{ color: 'var(--ink)', fontWeight: 700 }}>{filtered.length}</span> {filtered.length === 1 ? 'spot' : 'spots'}
                {' · '}
                <span style={{ color: 'var(--ink)', fontWeight: 700 }}>{neighborhoodCount}</span> {neighborhoodCount === 1 ? 'neighborhood' : 'neighborhoods'}
                {cheapest != null && (
                  <>
                    {' · '}
                    from <span style={{ color: 'var(--wine)', fontWeight: 700 }}>{formatMoney(cheapest)}</span>/person
                  </>
                )}
              </>
            )}
        </div>
        <div className="ga-view-toggle" role="tablist" aria-label="Result view">
          <button role="tab" aria-selected={view === 'list'} className={view === 'list' ? 'is-active' : ''} onClick={() => setView('list')}>
            <span aria-hidden>▦</span> List
          </button>
          <button role="tab" aria-selected={view === 'map'} className={view === 'map' ? 'is-active' : ''} onClick={() => setView('map')}>
            <span aria-hidden>◉</span> Map
          </button>
        </div>
      </div>

      {/* Results */}
      {!loading && filtered.length === 0 ? (
        <div style={{ marginTop: 24, padding: 40, borderRadius: 20, background: 'var(--card)', textAlign: 'center' }}>
          <div className="display" style={{ fontSize: 26, marginBottom: 8 }}>
            Nothing matches <span className="serif-italic" style={{ color: 'var(--wine)' }}>that combo.</span>
          </div>
          <p className="muted" style={{ margin: 0 }}>Try a different night, neighborhood, or headcount.</p>
        </div>
      ) : view === 'map' ? (
        <div style={{ marginTop: 20, display: 'grid', gap: 20, gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)' }} className="ga-map-split">
          <VendorMap vendors={filtered} height={620} onVendorClick={(slug) => navigate(`/locations/${slug}${linkSuffix}`)} />
          <div className="ga-map-list">
            {filtered.map((v) => <VendorResultCard key={v.id} vendor={v} linkSuffix={linkSuffix} compact />)}
          </div>
        </div>
      ) : (
        <div className="r-grid-3" style={{ marginTop: 24 }}>
          {filtered.map((v) => <VendorResultCard key={v.id} vendor={v} linkSuffix={linkSuffix} />)}
        </div>
      )}

      <HomeStyles />
    </div>
  );
}

function HomeStyles() {
  return (
    <style>{`
      .ga-search-pill {
        background: var(--bg);
        border: 1.5px solid var(--ink);
        border-radius: 999px;
        padding: 6px 8px 6px 18px;
        display: grid;
        grid-template-columns: 1.1fr auto 1.3fr auto 1.2fr auto 1.4fr;
        align-items: center;
        gap: 0;
        box-shadow: 0 18px 40px -30px rgba(0,0,0,0.35);
      }
      .ga-search-cell {
        padding: 10px 16px;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }
      .ga-search-cell input,
      .ga-search-cell select {
        border: none;
        background: transparent;
        padding: 0;
        font-size: 15px;
        font-weight: 600;
        color: var(--ink);
        height: auto;
      }
      .ga-search-cell input:focus,
      .ga-search-cell select:focus {
        outline: none;
        border: none;
      }
      .ga-search-label {
        font-family: ui-monospace, 'SF Mono', Menlo, monospace;
        font-size: 10px;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-soft);
      }
      .ga-search-divider {
        width: 1px;
        height: 36px;
        background: var(--line);
      }
      .ga-stepper {
        width: 28px;
        height: 28px;
        border-radius: 999px;
        border: 1.5px solid var(--line);
        background: transparent;
        cursor: pointer;
        font-size: 16px;
        font-weight: 700;
        color: var(--ink);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        transition: border-color 0.15s ease, background 0.15s ease;
      }
      .ga-stepper:hover { border-color: var(--ink); background: var(--card); }

      .ga-vibe-scroll {
        display: flex;
        gap: 10px;
        overflow-x: auto;
        scrollbar-width: none;
        padding: 4px 0 6px;
        margin: 0 -8px;
        padding-left: 8px;
        padding-right: 8px;
        scroll-snap-type: x proximity;
      }
      .ga-vibe-scroll::-webkit-scrollbar { display: none; }
      .ga-vibe-chip {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        border-radius: 999px;
        border: 1.5px solid var(--line);
        background: var(--bg);
        color: var(--ink);
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
        scroll-snap-align: start;
        white-space: nowrap;
      }
      .ga-vibe-chip:hover {
        transform: translateY(-1px);
        border-color: var(--ink);
      }
      .ga-vibe-chip--active {
        background: var(--ink);
        color: var(--bg);
        border-color: var(--ink);
      }

      .ga-view-toggle {
        display: inline-flex;
        padding: 4px;
        background: var(--card);
        border-radius: 999px;
        gap: 2px;
      }
      .ga-view-toggle button {
        border: none;
        background: transparent;
        color: var(--ink-soft);
        padding: 8px 16px;
        border-radius: 999px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .ga-view-toggle button.is-active {
        background: var(--bg);
        color: var(--ink);
        box-shadow: 0 2px 8px -4px rgba(0,0,0,0.25);
      }

      .ga-map-list {
        display: flex;
        flex-direction: column;
        gap: 14px;
        max-height: 620px;
        overflow-y: auto;
        padding-right: 6px;
      }

      .ga-rail {
        display: flex;
        gap: 16px;
        overflow-x: auto;
        scrollbar-width: none;
        padding: 4px 0 8px;
        scroll-snap-type: x mandatory;
      }
      .ga-rail::-webkit-scrollbar { display: none; }
      .ga-rail-card {
        flex: 0 0 320px;
        scroll-snap-align: start;
        position: relative;
        aspect-ratio: 16 / 10;
        border-radius: 18px;
        overflow: hidden;
        color: #fff;
        text-decoration: none;
        transition: transform 0.25s ease, box-shadow 0.25s ease;
        background-color: var(--card);
      }
      .ga-rail-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 24px 40px -28px rgba(0,0,0,0.5);
      }
      .ga-rail-card-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.78) 100%);
      }
      .ga-rail-card-body {
        position: absolute;
        left: 18px;
        right: 18px;
        bottom: 16px;
      }
      .ga-rail-card-day {
        display: inline-block;
        background: #fff;
        color: #1c1816;
        font-family: ui-monospace, 'SF Mono', Menlo, monospace;
        font-size: 10px;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        padding: 4px 10px;
        border-radius: 999px;
        margin-bottom: 8px;
        font-weight: 700;
      }

      .ga-result {
        display: block;
        text-decoration: none;
        color: var(--ink);
        transition: transform 0.25s ease;
      }
      .ga-result:hover { transform: translateY(-4px); }
      .ga-result-photo {
        position: relative;
        width: 100%;
        aspect-ratio: 4 / 3;
        border-radius: 18px;
        overflow: hidden;
        background-color: var(--card);
      }
      .ga-result-photo::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%);
      }
      .ga-result-price {
        position: absolute;
        top: 12px;
        right: 12px;
        background: #fff;
        color: #1c1816;
        padding: 7px 12px;
        border-radius: 999px;
        font-weight: 700;
        font-size: 13px;
        z-index: 1;
        box-shadow: 0 4px 12px -4px rgba(0,0,0,0.35);
      }
      .ga-result-price small {
        font-weight: 500;
        font-size: 10px;
        color: #666;
        margin-left: 3px;
      }
      .ga-result-vibes {
        position: absolute;
        left: 12px;
        top: 12px;
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
        max-width: calc(100% - 100px);
        z-index: 1;
      }
      .ga-result-vibes span {
        background: rgba(0,0,0,0.55);
        backdrop-filter: blur(6px);
        color: #fff;
        font-size: 10px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        font-family: ui-monospace, 'SF Mono', Menlo, monospace;
        padding: 4px 8px;
        border-radius: 999px;
        font-weight: 600;
      }

      .ga-result-compact {
        display: grid;
        grid-template-columns: 100px 1fr;
        gap: 14px;
        padding: 10px;
        border-radius: 14px;
        background: var(--card);
        text-decoration: none;
        color: var(--ink);
        align-items: center;
        transition: transform 0.18s ease, background 0.18s ease;
      }
      .ga-result-compact:hover {
        transform: translateX(2px);
        background: color-mix(in oklch, var(--card) 85%, var(--ink) 15%);
      }
      .ga-result-compact-photo {
        width: 100px;
        height: 100px;
        border-radius: 10px;
        overflow: hidden;
      }

      @media (max-width: 960px) {
        .ga-search-pill {
          border-radius: 22px;
          grid-template-columns: 1fr 1fr;
          padding: 8px;
          row-gap: 0;
        }
        .ga-search-divider { display: none; }
        .ga-search-cell { padding: 10px 12px; }
        .ga-search-cell-search { grid-column: 1 / -1; border-top: 1px solid var(--line); }
        .ga-map-split { grid-template-columns: 1fr !important; }
        .ga-map-list { max-height: none; }
        .ga-rail-card { flex-basis: 280px; }
      }
    `}</style>
  );
}

function UpcomingRail({ bookings, loading }: { bookings: BookingWithJoins[]; loading: boolean }) {
  if (loading) return null;
  if (bookings.length === 0) return null;

  return (
    <div style={{ marginBottom: 36 }}>
      <div className="row-between" style={{ marginBottom: 14 }}>
        <div>
          <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 6 }}>
            Up next · {bookings.length}
          </div>
          <h2 className="display" style={{ fontSize: 22, margin: 0 }}>Your team's <span className="serif-italic" style={{ color: 'var(--wine)' }}>lined-up nights.</span></h2>
        </div>
        <Link to="/dashboard/bookings" className="btn btn-ghost btn-sm">All bookings →</Link>
      </div>
      <div className="ga-rail">
        {bookings.map((b) => (
          <Link key={b.id} to={`/bookings/${b.id}`} className="ga-rail-card" style={{
            background: b.vendor?.hero_image_url
              ? `url(${b.vendor.hero_image_url}) center/cover no-repeat`
              : `linear-gradient(135deg, oklch(0.45 0.14 ${((b.vendor?.name ?? '?').charCodeAt(0) * 7) % 360}), oklch(0.62 0.16 ${(((b.vendor?.name ?? '?').charCodeAt(0) * 7) + 30) % 360}))`,
          }}>
            <div className="ga-rail-card-overlay" />
            <div className="ga-rail-card-body">
              <span className="ga-rail-card-day">{dayLabel(b.booking_date)} · {formatTime(b.booking_time)}</span>
              <div className="display" style={{ fontSize: 26, margin: 0, lineHeight: 1.1 }}>{b.vendor?.name ?? '—'}</div>
              <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>
                {b.vendor?.neighborhood} · {b.headcount} {b.headcount === 1 ? 'person' : 'people'}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function VendorResultCard({ vendor, linkSuffix = '', compact = false }: { vendor: VendorCard; linkSuffix?: string; compact?: boolean }) {
  const offer = vendor.offers[0];

  if (compact) {
    return (
      <Link to={`/locations/${vendor.slug}${linkSuffix}`} className="ga-result-compact">
        <div className="ga-result-compact-photo" style={{
          background: vendor.hero_image_url
            ? `url(${vendor.hero_image_url}) center/cover no-repeat`
            : `linear-gradient(135deg, oklch(0.72 0.12 ${(vendor.name.charCodeAt(0) * 7) % 360}), oklch(0.38 0.13 ${((vendor.name.charCodeAt(0) * 7) + 30) % 360}))`,
        }} />
        <div style={{ minWidth: 0 }}>
          <div className="display" style={{ fontSize: 16, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{vendor.name}</div>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginTop: 4 }}>
            {vendor.neighborhood}
          </div>
          {vendor.minPriceCents != null && (
            <div style={{ fontSize: 13, marginTop: 6 }}>
              From <strong style={{ color: 'var(--wine)' }}>{formatMoney(vendor.minPriceCents)}</strong> /person
            </div>
          )}
        </div>
      </Link>
    );
  }

  const photoFallback = `linear-gradient(135deg, oklch(0.72 0.12 ${(vendor.name.charCodeAt(0) * 7) % 360}), oklch(0.38 0.13 ${((vendor.name.charCodeAt(0) * 7) + 30) % 360}))`;

  return (
    <Link to={`/locations/${vendor.slug}${linkSuffix}`} className="ga-result">
      <div className="ga-result-photo" style={{
        background: vendor.hero_image_url
          ? `url(${vendor.hero_image_url}) center/cover no-repeat, ${photoFallback}`
          : photoFallback,
      }}>
        {vendor.vibes.length > 0 && (
          <div className="ga-result-vibes">
            {vendor.vibes.slice(0, 2).map((v) => <span key={v}>{v}</span>)}
          </div>
        )}
        {vendor.minPriceCents != null && (
          <div className="ga-result-price">
            {formatMoney(vendor.minPriceCents)}<small>/person</small>
          </div>
        )}
      </div>
      <div style={{ paddingTop: 14 }}>
        <div className="row-between">
          <h3 className="display" style={{ fontSize: 22, margin: 0 }}>{vendor.name}</h3>
          <span className="mono" style={{ fontSize: 11, letterSpacing: '0.12em', color: 'var(--ink-soft)', textTransform: 'uppercase' }}>{vendor.neighborhood}</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 4 }}>
          {vendor.address}
        </div>
        {offer && (
          <div style={{ fontSize: 13, color: 'var(--ink)', marginTop: 8 }}>
            {offer.title}
          </div>
        )}
      </div>
    </Link>
  );
}

function Bookings() {
  const { user, profile } = useAuth();
  const [bookings, setBookings] = useState<(Booking & { vendor: Vendor | null; offer: Offer | null })[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from('ga_bookings')
        .select('*')
        .order('booking_date', { ascending: false });
      const list = data ?? [];

      if (list.length === 0) {
        setBookings([]);
        setLoading(false);
        return;
      }

      const vendorIds = [...new Set(list.map((b) => b.vendor_id))];
      const offerIds = [...new Set(list.map((b) => b.offer_id))];
      const [{ data: vendors }, { data: offers }] = await Promise.all([
        supabase.from('ga_vendors').select('*').in('id', vendorIds),
        supabase.from('ga_offers').select('*').in('id', offerIds),
      ]);
      const vendorMap = new Map((vendors ?? []).map((v) => [v.id, v]));
      const offerMap = new Map((offers ?? []).map((o) => [o.id, o]));
      setBookings(list.map((b) => ({ ...b, vendor: vendorMap.get(b.vendor_id) ?? null, offer: offerMap.get(b.offer_id) ?? null })));
      setLoading(false);
    })();
  }, [user]);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.booking_date >= today);
  const past = bookings.filter((b) => b.booking_date < today);

  return (
    <div>
      <SectionHeader
        eyebrow={`${profile?.email_domain ?? 'You'} · ${bookings.length} bookings`}
        title="Your team nights"
        action={<Link to="/dashboard" className="btn btn-primary btn-sm">Plan a new night →</Link>}
      />

      {loading ? (
        <p className="muted">Loading…</p>
      ) : bookings.length === 0 ? (
        <EmptyState
          title="No bookings yet"
          body="Pick a bar and reserve your first round. We'll handle the tab and the RSVPs."
          cta={<Link to="/dashboard" className="btn btn-primary">Find a bar →</Link>}
        />
      ) : (
        <>
          <Subheader title={`Upcoming (${upcoming.length})`} />
          {upcoming.length === 0 ? (
            <p className="muted" style={{ marginBottom: 32 }}>Nothing scheduled. <Link to="/dashboard" style={{ textDecoration: 'underline' }}>Plan another night →</Link></p>
          ) : (
            <div className="stack" style={{ marginBottom: 32 }}>
              {upcoming.map((b) => <BookingCard key={b.id} b={b} />)}
            </div>
          )}

          {past.length > 0 && (
            <>
              <Subheader title={`Past (${past.length})`} />
              <div className="stack">
                {past.map((b) => <BookingCard key={b.id} b={b} />)}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function BookingCard({ b }: { b: Booking & { vendor: Vendor | null; offer: Offer | null } }) {
  return (
    <Link to={`/bookings/${b.id}`} style={{ display: 'block', padding: 24, borderRadius: 16, background: 'var(--card)' }}>
      <div className="row-between">
        <div>
          <div className="display" style={{ fontSize: 22 }}>{b.vendor?.name ?? '-'}</div>
          <div style={{ fontSize: 14, color: 'var(--ink-soft)', marginTop: 4 }}>
            {formatDate(b.booking_date)} · {formatTime(b.booking_time)} · {b.headcount} people · {b.offer?.title}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="display" style={{ fontSize: 22, color: 'var(--wine)' }}>{formatMoney(b.total_cents)}</div>
          <span className={`tag ${b.status === 'confirmed' ? 'tag-active' : b.status === 'cancelled' ? 'tag-cancelled' : 'tag-pending'}`}>{b.status}</span>
        </div>
      </div>
    </Link>
  );
}

function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState<{ user_id: string; full_name: string | null; email: string }[]>([]);
  const [orgName, setOrgName] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const orgRow = await supabase.from('ga_org_members').select('org_id').eq('user_id', user.id).maybeSingle();
      const orgId = orgRow.data?.org_id;
      if (!orgId) return;

      const { data: org } = await supabase.from('ga_organizations').select('*').eq('id', orgId).maybeSingle();
      setOrgName(org?.name ?? org?.domain ?? null);

      const { data: rows } = await supabase.from('ga_org_members').select('user_id').eq('org_id', orgId);
      const ids = (rows ?? []).map((r) => r.user_id);
      if (ids.length === 0) return;
      const { data: profiles } = await supabase.from('ga_user_profiles').select('id, email, full_name').in('id', ids);
      setMembers((profiles ?? []).map((p) => ({ user_id: p.id, email: p.email, full_name: p.full_name })));
    })();
  }, [user]);

  return (
    <div>
      <SectionHeader eyebrow={orgName ? `Team · ${orgName}` : 'Team'} title="Everyone on Goaround at your company" />
      <p className="muted" style={{ marginBottom: 24 }}>
        Everyone who's signed in with an <strong>@{user?.email?.split('@')[1]}</strong> email shows up here. Bookings made by anyone on this list are visible to the whole team.
      </p>
      <div className="stack-sm">
        {members.length === 0 ? (
          <p className="muted">Nobody else here yet. Invite a teammate by sharing <Link to="/login" style={{ textDecoration: 'underline' }}>the signup link</Link>.</p>
        ) : (
          members.map((m) => (
            <div key={m.user_id} style={{ padding: 16, borderRadius: 12, background: 'var(--card)', display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 500 }}>{m.full_name || m.email}</div>
                {m.full_name && <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{m.email}</div>}
              </div>
              {m.user_id === user?.id && <span className="tag">you</span>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Payment() {
  return (
    <div>
      <SectionHeader eyebrow="Payment" title="Payment methods" />
      <div style={{ padding: 32, borderRadius: 18, background: 'var(--card)' }}>
        <div className="display" style={{ fontSize: 22, marginBottom: 8 }}>Stripe integration coming</div>
        <p className="muted">
          In this build, bookings run in demo mode. No card is charged. The Stripe checkout drops in next, so each booking will charge your company card up front and Goaround will settle with the bar after the event.
        </p>
      </div>
    </div>
  );
}

function SectionHeader({ eyebrow, title, action }: { eyebrow: string; title: string; action?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', marginBottom: 28, gap: 24, flexWrap: 'wrap' }}>
      <div>
        <div className="eyebrow" style={{ marginBottom: 12 }}>{eyebrow}</div>
        <h1 className="display" style={{ fontSize: 'clamp(32px, 4vw, 48px)', margin: 0 }}>{title}</h1>
      </div>
      {action}
    </div>
  );
}

function Subheader({ title }: { title: string }) {
  return (
    <div className="mono" style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginTop: 24, marginBottom: 16 }}>
      {title}
    </div>
  );
}

function EmptyState({ title, body, cta }: { title: string; body: string; cta?: React.ReactNode }) {
  return (
    <div style={{ padding: 40, borderRadius: 18, background: 'var(--card)', textAlign: 'center' }}>
      <div className="display" style={{ fontSize: 28, marginBottom: 8 }}>{title}</div>
      <p className="muted" style={{ maxWidth: 480, margin: '0 auto 20px' }}>{body}</p>
      {cta}
    </div>
  );
}
